import type { ForwardRefExoticComponent, RefAttributes } from "react";
import { forwardRef } from "react";

import type { SlotPressableProps, SlotProps } from "./types";
import { useSlot } from "./utils";

// Each Slot merges its props into its single child without adding a wrapper
// and composes the ref it was given with the child's own. The merge contract
// lives in ./utils (mergeProps, composeRefs), shared with native; the variants
// differ only in their types.

// forwardRef drops `ref` with Omit, which would also drop the declared props
// of a type with an index signature: render with those, export the full type
type OwnProps = Pick<SlotProps, "children" | "className" | "style">;
type OwnPressableProps = Pick<SlotPressableProps, "children" | "className" | "style">;

/**
 * SlotView - Web implementation. Used for View-like elements (div, section…).
 */
export const SlotView: ForwardRefExoticComponent<SlotProps & RefAttributes<unknown>> = forwardRef<
  unknown,
  OwnProps
>(({ children, ...props }, ref) => useSlot("SlotView", children, props, ref));

SlotView.displayName = "SlotView";

/**
 * SlotText - Web implementation. Used for Text-like elements (span, p, h1-h6…).
 */
export const SlotText: ForwardRefExoticComponent<SlotProps & RefAttributes<unknown>> = forwardRef<
  unknown,
  OwnProps
>(({ children, ...props }, ref) => useSlot("SlotText", children, props, ref));

SlotText.displayName = "SlotText";

/**
 * SlotPressable - Web implementation. Supports state-callback `style` and
 * `className`, composed for a callback-capable child to resolve.
 */
export const SlotPressable: ForwardRefExoticComponent<SlotPressableProps & RefAttributes<unknown>> =
  forwardRef<unknown, OwnPressableProps>(({ children, ...props }, ref) =>
    useSlot("SlotPressable", children, props, ref),
  );

SlotPressable.displayName = "SlotPressable";

/**
 * SlotImage - Web implementation. Used for Image elements.
 */
export const SlotImage: ForwardRefExoticComponent<SlotProps & RefAttributes<unknown>> = forwardRef<
  unknown,
  OwnProps
>(({ children, ...props }, ref) => useSlot("SlotImage", children, props, ref));

SlotImage.displayName = "SlotImage";

/**
 * Slot - Generic slot component (alias to SlotView)
 *
 * The variant names say what the child is. SlotView, SlotText and SlotImage
 * take the same props; SlotPressable adds press-state callbacks.
 */
export const Slot = SlotView;
